import axios from 'axios'
import { WeekdayNumbers } from 'luxon'
import { useRouter } from 'next/router'
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  CartInput,
  CartLineInput,
  CartLineUpdateInput,
} from '@data/shopify/storefront/types'
import { SanityInstitutionByIdQuery } from '@data/sanity/queries/types/institution'
import { SanityProductVariantQuery } from '@data/sanity/queries/types/product'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { SanitySiteStrings } from '@data/sanity/queries/types/strings'
import {
  addLineItemsToShopifyCart,
  createShopifyCart,
  removeLineItemsFromShopifyCart,
  updateLineItemsInShopifyCart,
} from './shopify/graphql/cart'
import { ShopifyShop } from './shopify/graphql/shop'
import {
  getIsCartCompleted,
  getTaxCartAttributes,
  institutionIdAttributeKey,
  parseShopifyCart,
  shopIdAttributeKey,
} from './shopify/cart'
import {
  getCartAttributesForCheckout,
  getCartDeliveryAddressForCheckout,
  getCartNoteForCheckout,
  parseCartLineItemsForCheckout,
} from './shopify/checkout'
import { getShopifyGlobalId } from './shopify/client'
import {
  getInstitutionAttributes,
  validateLineItemInstitutionAttributes,
} from './shopify/institution'
import { getProductVariants } from './sanity/product'
import { getInstitutionById } from './sanity/institution'
import { DiscountContext } from './discount'
import { triggerAddToCartFacebookEvent } from './facebook'
import { ErrorMessages } from './helpers'
import { InstitutionContext } from './institution-context'
import { LanguageContext, Locale } from './language'
import { getShopifyCartIdStorageKey } from './local-storage'
import { getPartnerAdsAttributes } from './partner-ads'
import { PrintShopContext } from './print/print-shop-context'
import { getPageUrl, PageType } from './routes'
import { getEstimatedDeliveryDate } from './shipping'
import { ShopContext } from './shop-context'
import { StringsContext } from './strings-context'

import { ValidateVatIdResult } from '@pages/api/tax/validate-vat-id'

export interface CartFormValues {
  vatId: string
  comment: string
  institutionId: string
}

interface ValidateCartProps {
  errors: ErrorMessages
  vatIdCountryCode: string | null
}

interface CartTotals {
  subTotal: number
  totalDiscount: number
  total: number
}

export interface LineItemAttribute {
  key: string
  value: string
}

export interface LineItem extends SanityProductVariantQuery {
  lineId: string
  merchandiseId: string
  quantity: number
  attributes?: LineItemAttribute[]
}

export interface VariantLineItem {
  id: number
  price: number
  quantity: number
}

export interface CartLineItem {
  id: number
  quantity: number
  attributes?: LineItemAttribute[]
}

interface CartUpdateLineItem {
  id: string
  quantity: number
  attributes?: LineItemAttribute[]
}

export interface AutomaticDiscount {
  title: string
  amount: number
}

export interface Cart {
  id: string
  webUrl: string
  note: string
  lineItems: LineItem[]
  subTotal: number
  total: number
  automaticDiscount: AutomaticDiscount
  discountCodes: string[]
}

export interface CartDiscountUpdateResponse {
  error?: string
  cart?: Cart
}

interface SubmitCartResult {
  errors: ErrorMessages
  checkoutUrl: string
}

interface CartContextProps {
  cart: Cart
  isCartOpen: boolean
  isCartUpdating: boolean
  isCartProductAdding: boolean
  isCartSubmitting: boolean
  cartLineItemInstitutionId: string | null
  toggleCart: (newState: boolean) => void
  addItemsToCart: (
    getVariantLineItems: () => Promise<CartLineItem[]>,
  ) => Promise<boolean>
  updateCartItems: (variantLineItems: CartUpdateLineItem[]) => Promise<boolean>
  removeItemsFromCart: (ids: string[]) => Promise<boolean>
  submitCart: (values: CartFormValues) => Promise<SubmitCartResult>
  openCartInModal?: boolean
}

const emptyCart: Cart = {
  id: '',
  webUrl: '',
  note: '',
  lineItems: [],
  subTotal: 0,
  total: 0,
  automaticDiscount: {
    title: '',
    amount: 0,
  },
  discountCodes: [],
}

interface CartContextProviderProps {
  site: SanitySiteFragment
  shop: ShopifyShop | null
  children: ReactNode
}

export const CartContext = createContext<CartContextProps>({
  cart: emptyCart,
  isCartOpen: false,
  isCartUpdating: false,
  isCartProductAdding: false,
  isCartSubmitting: false,
  cartLineItemInstitutionId: null,
  toggleCart: () => null,
  addItemsToCart: async () => false,
  updateCartItems: async () => false,
  removeItemsFromCart: async () => false,
  submitCart: async () => ({
    errors: {},
    checkoutUrl: '',
  }),
})

/**
 * Validates cart form.
 */
const validateCart = async (
  strings: SanitySiteStrings,
  values: CartFormValues,
  institutionPickup: boolean,
) => {
  const results: ValidateCartProps = {
    errors: {},
    vatIdCountryCode: null,
  }

  // Validate VAT number
  if (values.vatId) {
    const validationResult = await validateVatId(values.vatId)
    results.vatIdCountryCode = validationResult?.countryCode ?? null

    if (!validationResult?.isValid) {
      results.errors.vatId = !validationResult
        ? strings.cartVatIdError
        : strings.cartInvalidVatIdError
    }
  }

  // Require institution, if enabled in settings
  if (institutionPickup && !values.institutionId) {
    results.errors.institutionId = strings.cartSchoolIdError
  }

  return results
}

/**
 * Calls API route that handles cart discount updating.
 */
const updateCartDiscount = async (
  locale: Locale,
  cartId: string,
): Promise<Cart | undefined> => {
  try {
    const discountUpdateResult = await axios.get<CartDiscountUpdateResponse>(
      '/api/shopify/cart-discount-update',
      {
        params: {
          cart_id: cartId,
        },
        headers: {
          'Content-Type': 'application/json',
          'X-Locale': locale,
        },
      },
    )

    if (discountUpdateResult.data?.error || !discountUpdateResult.data?.cart) {
      throw new Error(discountUpdateResult.data.error ?? 'Unknown error')
    }

    return discountUpdateResult.data.cart
  } catch (error) {
    console.error(error)
  }
}

/**
 * The cart context provider.
 */
export const CartContextProvider = ({
  site,
  shop,
  children,
}: CartContextProviderProps) => {
  const router = useRouter()
  const { institutionId, institutions } = useContext(InstitutionContext)
  const { locale } = useContext(LanguageContext)
  const { printShopInstitutionId } = useContext(PrintShopContext)
  const { countryCode, shopifyStorefrontClient } = useContext(ShopContext)
  const strings = useContext(StringsContext)

  const [cart, setCart] = useState<Cart>(emptyCart)
  const [openCartInModal, setOpenCartInModal] = useState(
    !site.cart?.openInSeparatePage,
  )
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [isCartUpdating, setIsCartUpdating] = useState(false)
  const [isCartProductAdding, setIsCartProductAdding] = useState(false)
  const [isCartSubmitting, setIsCartSubmitting] = useState(false)
  const [localeInitialised, setLocaleInitialised] = useState('')

  const deliveryDate =
    site.cart.shipping?.orderProcessingDay &&
    site.cart.shipping?.deliveryTimeInDays
      ? getEstimatedDeliveryDate(
          locale,
          site.cart.shipping.orderProcessingDay as WeekdayNumbers,
          site.cart.shipping.deliveryTimeInDays,
        ).toFormat('dd/MM/yyyy')
      : null

  const cartLineItemInstitutionId = useMemo<string | null>(() => {
    const shopInstitutionIds = cart.lineItems
      // Filter line items that are created from a private shop
      .filter(
        ({ attributes }) =>
          !!attributes?.find(
            (attribute) => attribute.key === `_${shopIdAttributeKey}`,
          )?.value,
      )
      // Map line items to institution IDs from attributes
      .map(
        ({ attributes }) =>
          attributes?.find(
            (attribute) => attribute.key === `_${institutionIdAttributeKey}`,
          )?.value ?? null,
      )
      .filter(Boolean) as string[]

    return shopInstitutionIds[0] ?? null
  }, [cart.lineItems])

  // Update callbacks
  const saveCart = useCallback((locale: Locale, cart?: Cart) => {
    if (!cart) {
      return
    }

    setCart(cart)

    if (typeof window !== `undefined`) {
      localStorage.setItem(getShopifyCartIdStorageKey(locale), cart.id)
    }
  }, [])

  const toggleCart = useCallback(
    (newState: boolean) => {
      if (!openCartInModal) {
        if (newState) {
          router.push(getPageUrl(PageType.CART_PAGE))
        }

        return
      }

      if (isCartOpen !== newState) {
        setIsCartOpen(newState)
      }
    },
    [isCartOpen, openCartInModal, router],
  )

  const addItemsToCart = useCallback(
    async (getVariantLineItems: () => Promise<CartLineItem[]>) => {
      if (!cart.id) {
        return false
      }

      if (!shopifyStorefrontClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartProductAdding(true)
      setIsCartUpdating(true)

      try {
        // Get variant details
        const variantLineItems = await getVariantLineItems()
        const variantIds = variantLineItems.map(
          (variantLineItem) => variantLineItem.id,
        )
        const productVariants = await getProductVariants(locale, variantIds)

        const cartPartnerAdsAttributes = getPartnerAdsAttributes(true)
        const lines: CartLineInput[] = variantLineItems.map(
          (variantLineItem) => {
            const variant = productVariants.find(
              (variant) => variant.id === variantLineItem.id,
            )

            return {
              merchandiseId: getShopifyGlobalId(
                'ProductVariant',
                variantLineItem.id,
              ),
              quantity: variantLineItem.quantity,
              attributes: [
                ...(variantLineItem.attributes ?? []),
                ...cartPartnerAdsAttributes,
              ],
            }
          },
        )
        const cartResponse = await addLineItemsToShopifyCart(
          shopifyStorefrontClient,
          cart.id,
          lines,
        )

        if (cartResponse.error) {
          return false
        }

        if (site.settings.facebookEvents) {
          productVariants.forEach(async (variant) => {
            await triggerAddToCartFacebookEvent(locale, variant)
          })
        }

        // Update cart discount codes
        const newCart = await updateCartDiscount(locale, cart.id)

        if (!newCart) {
          return false
        }

        saveCart(locale, newCart)

        setIsCartProductAdding(false)
        setIsCartUpdating(false)
        toggleCart(false)

        return !!newCart
      } catch (error) {
        console.error(error)

        setIsCartProductAdding(false)
        setIsCartUpdating(false)

        return false
      }
    },
    [
      cart.id,
      locale,
      saveCart,
      shopifyStorefrontClient,
      site.settings.facebookEvents,
      toggleCart,
    ],
  )

  const updateCartItems = useCallback(
    async (variantLineItems: CartUpdateLineItem[]): Promise<boolean> => {
      if (!cart.id) {
        return false
      }

      if (!shopifyStorefrontClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartUpdating(true)

      // Update cart line items
      const cartPartnerAdsAttributes = getPartnerAdsAttributes(true)
      const lines: CartLineUpdateInput[] = variantLineItems.map(
        ({ id, quantity, attributes }) => ({
          id,
          quantity,
          attributes: [...(attributes ?? []), ...cartPartnerAdsAttributes],
        }),
      )
      const cartResponse = await updateLineItemsInShopifyCart(
        shopifyStorefrontClient,
        cart.id,
        lines,
      )

      if (cartResponse.error) {
        return false
      }

      // Update cart discount codes
      const newCart = await updateCartDiscount(locale, cart.id)

      if (!newCart) {
        return false
      }

      saveCart(locale, newCart)

      setIsCartUpdating(false)

      return !!newCart
    },
    [cart.id, locale, saveCart, shopifyStorefrontClient],
  )

  const removeItemsFromCart = useCallback(
    async (ids: string[]): Promise<boolean> => {
      if (!cart.id) {
        return false
      }

      if (!shopifyStorefrontClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartUpdating(true)

      // Remove line item from Shopify cart
      const cartResponse = await removeLineItemsFromShopifyCart(
        shopifyStorefrontClient,
        cart.id,
        ids,
      )

      if (cartResponse.error) {
        return false
      }

      // Update cart discount codes
      const newCart = await updateCartDiscount(locale, cart.id)

      if (!newCart) {
        return false
      }

      saveCart(locale, newCart)

      setIsCartUpdating(false)

      return !!newCart
    },
    [cart.id, locale, saveCart, shopifyStorefrontClient],
  )

  const submitCart = useCallback(
    async (values: CartFormValues): Promise<SubmitCartResult> => {
      if (!shopifyStorefrontClient) {
        throw new Error('Shopify Storefront API client missing')
      }

      setIsCartSubmitting(true)

      // Validate cart form
      const { errors, vatIdCountryCode } = await validateCart(
        strings,
        values,
        !!site.cart.shipping?.schoolPickup,
      )
      const errorList = Object.entries(errors)

      if (errorList.length > 0) {
        setIsCartSubmitting(false)
        return {
          checkoutUrl: '',
          errors,
        }
      }

      let institution: SanityInstitutionByIdQuery = null

      if (values.institutionId) {
        institution = await getInstitutionById(values.institutionId)
      }

      // Get checkout note
      let cartNote = getCartNoteForCheckout(
        strings,
        values.comment,
        institution,
      )
      cartNote = `${cartNote}\nPlanned delivery: ${deliveryDate}`

      // Get checkout attributes
      const partnerAdsAttributes = getPartnerAdsAttributes()
      const taxAttributes = getTaxCartAttributes(
        values.vatId,
        vatIdCountryCode !== countryCode,
      )
      const cartAttributesForCheckout = getCartAttributesForCheckout(cart.id)

      // Create new cart for checkout
      const lines = await parseCartLineItemsForCheckout(
        locale,
        strings,
        cart.lineItems,
      )
      const cartInput: CartInput = {
        lines,
        attributes: [
          ...partnerAdsAttributes,
          ...taxAttributes,
          ...cartAttributesForCheckout,
        ],
        note: cartNote,
      }

      // Get delivery address
      const cartDeliveryAddress = getCartDeliveryAddressForCheckout(
        locale,
        cart.lineItems,
        !!site.cart.shipping?.schoolPickup,
        institution,
      )

      if (cartDeliveryAddress) {
        cartInput.delivery = {
          addresses: [cartDeliveryAddress],
        }
      }

      // Create a new cart and get its checkout URL
      try {
        const createShopifyCartResponse = await createShopifyCart(
          shopifyStorefrontClient,
          cartInput,
        )

        setIsCartSubmitting(false)

        return {
          checkoutUrl: createShopifyCartResponse.cart?.checkoutUrl ?? '',
          errors: {},
        }
      } catch (error) {
        console.error(error)

        setIsCartSubmitting(false)

        return {
          checkoutUrl: '',
          errors: {
            checkout: `${error}`,
          },
        }
      }
    },
    [
      cart.id,
      cart.lineItems,
      countryCode,
      deliveryDate,
      locale,
      shopifyStorefrontClient,
      site,
      strings,
    ],
  )

  // Load initial cart from Shopify
  useEffect(() => {
    if (!shop || localeInitialised === locale) {
      return
    }

    if (!shopifyStorefrontClient) {
      throw new Error('Shopify Storefront API client missing')
    }

    setLocaleInitialised(locale)

    const loadCart = async () => {
      const shopCart = 'cart' in shop && shop.cart ? shop.cart : null
      const isCartCompleted = getIsCartCompleted(shopCart)

      // Check if cart is found and is completed
      if (shopCart && !isCartCompleted) {
        const currentCart = await parseShopifyCart(locale, shopCart)

        if (currentCart) {
          saveCart(locale, currentCart)
          return
        }
      }

      // Delete saved cart ID
      localStorage.removeItem(getShopifyCartIdStorageKey(locale))

      // Create a new cart
      const createShopifyCartResponse = await createShopifyCart(
        shopifyStorefrontClient,
        {
          note: '',
        },
      )
      const createdCart = await parseShopifyCart(
        locale,
        createShopifyCartResponse.cart,
      )

      saveCart(locale, createdCart)
    }
    loadCart()
  }, [locale, localeInitialised, saveCart, shop, shopifyStorefrontClient])

  // Load cart settings (update when switching language)
  useEffect(() => {
    setOpenCartInModal(!site.cart?.openInSeparatePage)
  }, [site.cart?.openInSeparatePage])

  // Validate and update cart line item attributes
  useEffect(() => {
    // Wait for cart and line items to load
    if (!cart.id || !cart.lineItems || cart.lineItems.length === 0) {
      return
    }

    // Validate line item institution IDs and institution names
    const currentInstitutionId =
      cartLineItemInstitutionId ?? printShopInstitutionId ?? institutionId
    const isInstitutionValid =
      !currentInstitutionId ||
      validateLineItemInstitutionAttributes(
        cart.lineItems,
        currentInstitutionId,
      )

    if (isInstitutionValid) {
      return
    }

    // Get new institution attributes
    const institution = institutions.find(
      (institution) => institution.id === currentInstitutionId,
    )
    const institutionAttributes = institution
      ? getInstitutionAttributes(institution.id, institution.name)
      : []

    // Get new attributes and their keys
    const newAttributes = [...institutionAttributes]
    const newAttributeKeys = newAttributes.map((attribute) => attribute.key)

    // Replace line item attributes
    const newLineItems: CartUpdateLineItem[] = cart.lineItems.map(
      (lineItem) => {
        // Get current attributes without new attribute keys
        const currentAttributes =
          lineItem.attributes?.filter(
            (attribute) => !newAttributeKeys.includes(attribute.key),
          ) ?? []

        return {
          id: lineItem.lineId,
          quantity: lineItem.quantity,
          attributes: [...currentAttributes, ...newAttributes],
        }
      },
    )

    updateCartItems(newLineItems)
  }, [
    cart,
    cartLineItemInstitutionId,
    institutionId,
    institutions,
    printShopInstitutionId,
    updateCartItems,
  ])

  return (
    <CartContext.Provider
      value={{
        cart,
        isCartOpen,
        isCartUpdating,
        isCartProductAdding,
        isCartSubmitting,
        cartLineItemInstitutionId,
        toggleCart,
        addItemsToCart,
        updateCartItems,
        removeItemsFromCart,
        submitCart,
        openCartInModal,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

/**
 * Returns cart item count.
 */
export const useCartItemCount = () => {
  const { cart } = useContext(CartContext)

  return useMemo(() => {
    const lineItems = cart?.lineItems ?? []

    return lineItems
      .filter((lineItem) => lineItem.product?.type !== 'fee')
      .reduce((total, lineItem) => total + lineItem.quantity, 0)
  }, [cart?.lineItems])
}

/**
 * Returns cart totals.
 */
export const useCartTotals = (): CartTotals => {
  const { cart } = useContext(CartContext)
  const { cartDiscountItems } = useContext(DiscountContext)

  return useMemo(
    () => ({
      subTotal: cart?.subTotal ?? 0,
      totalDiscount:
        cartDiscountItems?.reduce(
          (total, { amount, quantity }) => total + amount * quantity,
          0,
        ) ?? 0,
      total: cart?.total ?? 0,
    }),
    [cart?.subTotal, cart?.total, cartDiscountItems],
  )
}

/**
 * Validates VAT ID using API route.
 */
export const validateVatId = async (vatId: CartFormValues['vatId']) => {
  try {
    const validationResult = await axios.get<ValidateVatIdResult>(
      '/api/tax/validate-vat-id',
      {
        params: {
          id: vatId,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    return validationResult.data
  } catch (_) {
    return
  }
}

/**
 * Returns boolean whether given attributes exist
 */
export const lineItemAttributesExist = (
  lineItem: CartLineItem,
  attributeKeys: string[],
) => {
  return attributeKeys.every((attributeKey) =>
    lineItem.attributes?.map(({ key }) => key).includes(`_${attributeKey}`),
  )
}
